import {
    type FC
} from "react";
import type IDataTableDensityToggleProps from "./type";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useDataTableContext
} from "../../context";
import type {
    TableSize
} from "../../../table/type";
import {
    Rows2 as Rows2Icon,
    Rows3 as Rows3Icon,
    Rows4 as Rows4Icon
} from "lucide-react-native";
import ToggleGroup from "../../../toggleGroup";

const DENSITY_ITEMS = [
    {
        titleKey: "data-table-density-compact",
        Icon: Rows4Icon,
        value: "small"
    },
    {
        titleKey: "data-table-density-medium",
        Icon: Rows3Icon,
        value: "medium"
    },
    {
        titleKey: "data-table-density-large",
        Icon: Rows2Icon,
        value: "large"
    }
] as const;

const getIsTableSize = (value: unknown): value is TableSize => {
    return DENSITY_ITEMS.some((item) => item.value === value);
};

const DataTableDensityToggle: FC<IDataTableDensityToggleProps> = ({
    customTheme: customThemeProp,
    style
}) => {
    const context = useDataTableContext({
        part: "DensityToggle"
    });

    const {
        colors
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context?.customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    if(!context || context.layout === "cards") {
        return null;
    }

    const onChange = (value: string | Array<string>) => {
        if(getIsTableSize(value)) {
            context.handleSizeChange(value);
        }
    };

    return <ToggleGroup
        customTheme={customThemeProp ?? context.customTheme}
        isDisabled={context.isPermissionDenied}
        title={localize("data-table-density")}
        attachBehaviour="attached"
        selectBehaviour="single"
        onValueChange={onChange}
        isClearEnabled={false}
        value={context.size}
        variant="outline"
        style={style}
        size="small"
    >
        {
            DENSITY_ITEMS.map(({
                titleKey,
                value,
                Icon
            }) => {
                const renderIcon: NCoreUIKitIcon = ({
                    customColor,
                    color,
                    size
                }) => <Icon
                    color={customColor ?? colors.content.icon[color]}
                    size={size}
                />;

                return <ToggleGroup.Item
                    title={localize(titleKey)}
                    icon={renderIcon}
                    isIconOnly={true}
                    value={value}
                    key={value}
                />;
            })
        }
    </ToggleGroup>;
};
export default DataTableDensityToggle;
